import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MessageCircle, Send, Copy, Link as LinkIcon, ExternalLink } from "lucide-react";

export default function EducationParents() {
  const [threads, setThreads] = useState([]);
  const [eleves, setEleves] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ eleve_id: "", title: "", message: "" });
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");

  const load = async () => {
    try {
      const [t, e] = await Promise.all([
        api.get("/chef/education/parent-threads"),
        api.get("/chef/education/eleves"),
      ]);
      setThreads(t.data);
      setEleves(e.data);
      if (selected) {
        const fresh = t.data.find((x) => x.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.eleve_id || !form.title || !form.message) return;
    try {
      const r = await api.post("/chef/education/parent-threads", form);
      toast.success("Conversation ouverte");
      setOpen(false);
      setForm({ eleve_id: "", title: "", message: "" });
      setSelected(r.data);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    try {
      await api.post(`/chef/education/parent-threads/${selected.id}/reply`, { message: reply.trim() });
      setReply("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const buildParentLink = (t) => `${window.location.origin}/parent/${t.parent_token}`;
  const copyLink = (t) => {
    navigator.clipboard.writeText(buildParentLink(t));
    toast.success("Lien copié — envoyez-le au parent");
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Éducation · Parents"
        title="Messagerie parents"
        sub="Échangez en privé avec les parents. Chaque conversation a un lien magique à partager."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="parent-thread-new-btn" className="h-11 bg-[#002FA7] text-white"><Plus className="h-4 w-4 mr-2" /> Nouvelle conversation</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Nouvelle conversation</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="parent-thread-form">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Élève *</Label>
                  <Select value={form.eleve_id} onValueChange={(v) => setForm({ ...form, eleve_id: v })}>
                    <SelectTrigger className="h-11 mt-1" data-testid="pt-eleve"><SelectValue placeholder="Sélectionner un élève" /></SelectTrigger>
                    <SelectContent>{eleves.map((e) => <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom} {e.parent_nom ? `— ${e.parent_nom}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Titre *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-11 mt-1" required data-testid="pt-title" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Premier message *</Label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="mt-1" required data-testid="pt-msg" />
                </div>
                <DialogFooter><Button type="submit" className="bg-[#002FA7] text-white" data-testid="pt-submit">Créer & ouvrir</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="p-6 lg:p-10 grid lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-4 bg-white border border-[#DEE2E6]" data-testid="threads-list">
          <div className="p-4 border-b border-[#DEE2E6] text-xs uppercase tracking-[0.25em] font-bold flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-[#002FA7]" /> Conversations ({threads.length})
          </div>
          {threads.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]">Aucune conversation.</div>
          ) : threads.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelected(t)}
              data-testid={`thread-${t.id}`}
              className={`w-full text-left p-4 border-b border-[#F8F9FA] last:border-b-0 hover:bg-[#F8F9FA] ${selected?.id === t.id ? "bg-[#F8F9FA] border-l-4 border-l-[#002FA7]" : ""}`}
            >
              <div className="flex justify-between items-start">
                <div className="font-bold text-sm">{t.title}</div>
                {t.unread_for_chef && <span className="h-2 w-2 rounded-full bg-[#FFD400]" />}
              </div>
              <div className="text-[10px] text-[#868E96] mt-1">{t.eleve_nom} {t.parent_nom && `· ${t.parent_nom}`}</div>
              <div className="text-[10px] text-[#495057] mt-1 font-mono">{(t.messages || []).length} msg · {new Date(t.updated_at).toLocaleDateString("fr-FR")}</div>
            </button>
          ))}
        </div>

        {/* Detail */}
        <div className="lg:col-span-8 bg-white border border-[#DEE2E6] flex flex-col" data-testid="thread-detail">
          {!selected ? (
            <div className="p-16 text-center text-sm text-[#868E96]">Sélectionnez une conversation pour la lire et y répondre.</div>
          ) : (
            <>
              <div className="p-5 border-b border-[#DEE2E6]">
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#868E96]">{selected.eleve_nom} {selected.parent_nom && `· ${selected.parent_nom}`}</div>
                    <h2 className="mt-1 font-display text-xl font-black tracking-tight">{selected.title}</h2>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => copyLink(selected)} data-testid="copy-parent-link" className="h-9 text-xs"><Copy className="h-3 w-3 mr-1" /> Lien parent</Button>
                    <a href={buildParentLink(selected)} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" data-testid="open-parent-link" className="h-9 text-xs"><ExternalLink className="h-3 w-3" /></Button>
                    </a>
                  </div>
                </div>
                <div className="mt-3 px-3 py-2 bg-[#FFFBEB] border border-[#FFD400]/30 text-[10px] font-mono break-all" data-testid="parent-link">
                  <LinkIcon className="h-3 w-3 inline mr-1 text-[#D97706]" />{buildParentLink(selected)}
                </div>
              </div>

              <div className="flex-1 p-5 space-y-3 overflow-y-auto" style={{ maxHeight: "500px" }} data-testid="thread-messages">
                {(selected.messages || []).map((m, i) => {
                  const isChef = m.from === "chef";
                  return (
                    <div key={i} className={`flex ${isChef ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] p-3 ${isChef ? "bg-[#002FA7] text-white" : "bg-[#F8F9FA] border border-[#DEE2E6]"}`}>
                        <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isChef ? "text-white/70" : "text-[#868E96]"}`}>
                          {m.from_name || (isChef ? "Vous" : "Parent")} · {new Date(m.created_at).toLocaleString("fr-FR")}
                        </div>
                        <div className="text-sm whitespace-pre-wrap">{m.message}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-4 border-t border-[#DEE2E6] flex gap-2">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2} placeholder="Votre réponse au parent…" className="flex-1" data-testid="chef-reply-input" />
                <Button onClick={sendReply} disabled={!reply.trim()} data-testid="chef-reply-submit" className="bg-[#002FA7] text-white"><Send className="h-4 w-4 mr-2" /> Envoyer</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
